import { useLayoutEffect, useRef, useState } from 'react';

const OPEN_MS = 280;
const CLOSE_MS = 200;
const EASING = 'cubic-bezier(0.2, 0, 0, 1)';

// Grows open when open turns true and shrinks shut when it turns false, so what's around it moves smoothly
// rather than at once; what's inside is only there while it shows. Once open, its content can overflow it
// again, as the toolbar's palettes do. Nothing moves for people who ask for less motion.
function Collapse({ open, children }) {
    const ref = useRef(null);
    const animation = useRef(null);
    const [shown, setShown] = useState(open);
    if (open && !shown) setShown(true);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const still = !el.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (still) {
            if (!open) setShown(false);
            return;
        }
        // From wherever it is, should it be turned around halfway.
        const from = animation.current ? el.getBoundingClientRect().height : (open ? 0 : el.offsetHeight);
        animation.current?.cancel();
        el.style.overflow = 'hidden';
        if (open) {
            const to = el.scrollHeight;
            animation.current = el.animate(
                [{ height: `${from}px`, opacity: from ? 1 : 0, transform: from ? 'none' : 'translateY(-6px)' }, { height: `${to}px`, opacity: 1, transform: 'none' }],
                { duration: OPEN_MS, easing: EASING },
            );
            animation.current.onfinish = () => {
                el.style.overflow = '';
                animation.current = null;
            };
        } else {
            animation.current = el.animate(
                [{ height: `${from}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
                { duration: CLOSE_MS, easing: EASING, fill: 'forwards' },
            );
            animation.current.onfinish = () => {
                animation.current = null;
                setShown(false);
            };
        }
    }, [open]);

    useLayoutEffect(() => () => animation.current?.cancel(), []);

    return shown ? <div ref={ref}>{children}</div> : null;
}

export { Collapse };
